import { useCallback, useEffect, useRef } from 'react'
import throttle from 'lodash/throttle'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const
const LAST_ACTIVITY_KEY = 'medconnect:lastActivity'
const TICK_MS = 1000

type UseIdleLogoutParams = {
  /** Inactivity window in milliseconds. */
  timeoutMs: number
  /** Only arm while the user is authenticated. */
  enabled: boolean
  /** Called once when the inactivity window elapses. */
  onLogout: () => void
  /** Show a warning this many ms BEFORE logout (0 = no warning). */
  warningMs?: number
  /** Called once when the warning window starts; receives the real remaining ms. */
  onWarn?: (remainingMs: number) => void
  /** Called whenever the timer is reset (hide the countdown dialog). */
  onActive?: () => void
}

function readLastActivity(): number {
  try {
    const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY))
    if (v) return v
  } catch {
    console.error('Failed to get last activity')
  }
  return Date.now()
}

function writeLastActivity(ts: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts))
  } catch {
    console.error('Failed to set last activity')
  }
}

export function useIdleLogout({ timeoutMs, enabled, onLogout, warningMs = 0, onWarn, onActive }: UseIdleLogoutParams) {
  const cbs = useRef({ onLogout, onWarn, onActive })
  cbs.current = { onLogout, onWarn, onActive }

  const timer = useRef({
    warned: false,
    tickId: null as ReturnType<typeof setInterval> | null,
    reset: () => { },
  })

  useEffect(() => {
    if (!enabled || !timeoutMs) return

    const warnLeadMs = warningMs > 0 ? Math.min(warningMs, Math.floor(timeoutMs / 2)) : 0

    const stop = () => {
      if (timer.current.tickId) clearInterval(timer.current.tickId)
      timer.current.tickId = null
    }

    const check = () => {
      const elapsed = Date.now() - readLastActivity()
      if (elapsed >= timeoutMs) {
        stop()
        cbs.current.onLogout()
        return
      }
      if (warnLeadMs > 0 && elapsed >= timeoutMs - warnLeadMs && !timer.current.warned) {
        timer.current.warned = true
        cbs.current.onWarn?.(timeoutMs - elapsed)
      }
    }

    const reset = () => {
      writeLastActivity(Date.now())
      timer.current.warned = false
      cbs.current.onActive?.()
    }
    timer.current.reset = reset

    const onActivity = () => {
      if (timer.current.warned) return
      reset()
    }

    const throttled = throttle(onActivity, 1000, { leading: true, trailing: true })

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      check()
    }

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, throttled, { passive: true }))
    document.addEventListener('visibilitychange', onVisibility)

    reset()
    timer.current.tickId = setInterval(check, TICK_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, throttled))
      document.removeEventListener('visibilitychange', onVisibility)
      throttled.cancel()
      stop()
    }
  }, [enabled, timeoutMs, warningMs])

  const reset = useCallback(() => timer.current.reset(), [])
  return { reset }
}
